/**
 * Brand-specific share & preview resolver for StudentsUnion AU platform
 * Accurately detects and generates rich messages and metadata for:
 *  - 🏠 RoomRoo (Rooms, Properties, Flatmates, Lease transfers)
 *  - 💼 HireMate (Jobs, Tasks, Staff Requests, Candidates)
 *  - 🚗 CarEase (Cars, Vehicle rentals)
 */

export const detectBrand = ({ url = '', category = '', title = '' }) => {
  const lowerUrl = String(url || '').toLowerCase();
  const lowerCat = String(category || '').toLowerCase();
  const lowerTitle = String(title || '').toLowerCase();

  // 1. CAR CHECK (CarEase)
  const isCar =
    lowerUrl.includes('/car') ||
    lowerUrl.includes('/carease') ||
    lowerCat.includes('car') ||
    lowerCat.includes('vehicle') ||
    lowerCat.includes('rental') ||
    lowerCat.includes('carease') ||
    lowerCat.includes('sedan') ||
    lowerCat.includes('suv') ||
    lowerCat.includes('hatchback');

  if (isCar) {
    return {
      brandKey: 'carease',
      brandName: 'CarEase',
      tagline: 'Student Car Rentals & Sharing in Australia',
      logoPath: '/carrentallogo.png',
      color: '#0284c7',
      bgLight: '#f0f9ff',
      borderColor: '#bae6fd',
      type: 'car'
    };
  }

  // 2. HIREMATE CHECK (Jobs, Tasks, Shifts, Candidates, Staff Requests)
  const isHiremate =
    lowerUrl.includes('/hiremate') ||
    lowerUrl.includes('/business/request') ||
    lowerUrl.includes('/staff') ||
    lowerCat.includes('job') ||
    lowerCat.includes('task') ||
    lowerCat.includes('hiremate') ||
    lowerCat.includes('staff') ||
    lowerCat.includes('shift') ||
    lowerCat.includes('casual') ||
    lowerCat.includes('candidate') ||
    lowerCat.includes('worker') ||
    lowerCat.includes('clean') ||
    lowerCat.includes('hospitality') ||
    lowerCat.includes('security') ||
    lowerCat.includes('retail') ||
    lowerCat.includes('driver') ||
    lowerCat.includes('logistics') ||
    lowerCat.includes('warehouse') ||
    lowerCat.includes('removalist') ||
    lowerCat.includes('gardening') ||
    lowerCat.includes('moving') ||
    lowerCat.includes('tutoring');

  if (isHiremate) {
    const isCandidate =
      lowerUrl.includes('worker=') ||
      lowerCat.includes('candidate') ||
      lowerCat.includes('worker');

    const isTask =
      (!lowerUrl.includes('/business/request') &&
        !lowerUrl.includes('/hiremate/jobs') &&
        !lowerCat.includes('staff') &&
        !lowerCat.includes('shift') &&
        (lowerUrl.includes('/hiremate/') || lowerCat.includes('task') || lowerCat.includes('gig'))) ||
      lowerCat === 'task' ||
      lowerCat.includes('user task');

    return {
      brandKey: 'hiremate',
      brandName: 'HireMate',
      tagline: 'Jobs, Tasks & Casual Work in Australia',
      logoPath: '/hirematelogo.png',
      color: '#0ab68e',
      bgLight: '#ecfdf5',
      borderColor: '#a7f3d0',
      type: isCandidate ? 'candidate' : isTask ? 'task' : 'job'
    };
  }

  // 3. ROOMROO CHECK (Rooms, Accommodations, Flatmates, Lease Transfer)
  const isRoom =
    lowerUrl.includes('/room') ||
    lowerUrl.includes('/flatmate') ||
    lowerUrl.includes('/lease-transfer') ||
    lowerUrl.includes('/property') ||
    lowerCat.includes('room') ||
    lowerCat.includes('property') ||
    lowerCat.includes('flatmate') ||
    lowerCat.includes('accommodation') ||
    lowerCat.includes('roomroo') ||
    lowerCat.includes('apartment') ||
    lowerCat.includes('studio') ||
    lowerCat.includes('lease');

  if (isRoom) {
    const isFlatmate = lowerUrl.includes('/flatmate') || lowerCat.includes('flatmate');
    const isLease = lowerUrl.includes('/lease') || lowerCat.includes('lease');
    return {
      brandKey: 'roomroo',
      brandName: 'RoomRoo',
      tagline: 'Student Accommodation & Flatmates',
      logoPath: '/roomroologo.png',
      color: '#ef4444',
      bgLight: '#fef2f2',
      borderColor: '#fecaca',
      type: isFlatmate ? 'flatmate' : isLease ? 'lease' : 'room'
    };
  }

  // 4. DEFAULT: StudentsUnion AU
  return {
    brandKey: 'studentunion',
    brandName: 'StudentsUnion',
    tagline: "Australia's #1 Student Community Platform",
    logoPath: '/logo.png',
    color: '#e11d48',
    bgLight: '#fff1f2',
    borderColor: '#fecdd3',
    type: 'general'
  };
};

/**
 * Builds server-side dynamic preview URL so WhatsApp crawler generates
 * the exact brand card (RoomRoo, HireMate, CarEase) instead of generic SU.
 */
export const getSharePreviewUrl = ({ url = '', brandKey = 'studentunion', title = '', price = '', location = '' }) => {
  const cleanUrl = String(url || '');
  const BACKEND_SHARE_BASE = 'https://api.studentsunionau.com';

  // 1. Direct ID matches for server-side DB lookups
  const roomMatch = cleanUrl.match(/\/(?:rooms|property)\/(\d+)/i);
  if (roomMatch) {
    return `${BACKEND_SHARE_BASE}/s/room/${roomMatch[1]}`;
  }

  const carMatch = cleanUrl.match(/\/cars\/(\d+)/i);
  if (carMatch) {
    return `${BACKEND_SHARE_BASE}/s/car/${carMatch[1]}`;
  }

  const jobMatch = cleanUrl.match(/\/(?:business\/request|hiremate\/jobs?)\/(\d+)/i);
  if (jobMatch) {
    return `${BACKEND_SHARE_BASE}/s/job/${jobMatch[1]}`;
  }

  const taskMatch = cleanUrl.match(/\/hiremate\/tasks?\/(\d+)/i);
  if (taskMatch) {
    return `${BACKEND_SHARE_BASE}/s/task/${taskMatch[1]}`;
  }

  const flatmateMatch = cleanUrl.match(/\/flatmates?\/(\d+)/i);
  if (flatmateMatch) {
    return `${BACKEND_SHARE_BASE}/s/flatmate/${flatmateMatch[1]}`;
  }

  // 2. Generic query bridge fallback
  const params = new URLSearchParams();
  if (brandKey) params.set('b', brandKey);
  if (cleanUrl) params.set('to', cleanUrl);
  if (title) params.set('t', title);
  if (price) params.set('p', String(price));
  if (location) params.set('l', String(location));

  return `${BACKEND_SHARE_BASE}/s?${params.toString()}`;
};

/**
 * Builds rich formatted WhatsApp text and plain text tailored for each brand.
 */
export const buildShareContent = ({
  url = '',
  title = '',
  category = '',
  price = '',
  location = '',
  description = ''
}) => {
  const brand = detectBrand({ url, category, title });
  const formattedPrice = price ? (typeof price === 'number' ? `$${price}` : String(price)) : '';
  const formattedLocation = location ? String(location) : '';
  const cleanCategory = category && category !== 'Listing' ? category : '';

  const previewUrl = getSharePreviewUrl({
    url,
    brandKey: brand.brandKey,
    title,
    price: formattedPrice,
    location: formattedLocation
  });

  let whatsappLines = [];
  let plainLines = [];
  let emailSubject = '';

  if (brand.brandKey === 'roomroo') {
    if (brand.type === 'flatmate') {
      whatsappLines = [
        `🤝 *RoomRoo Flatmate Profile*`,
        title ? `👤 *Looking For / Name:* "${title}"` : null,
        formattedLocation ? `📍 *Preferred Suburb:* ${formattedLocation}` : null,
        formattedPrice ? `💰 *Budget:* ${formattedPrice}` : null,
        cleanCategory ? `🏷️ *Category:* ${cleanCategory}` : null,
        '',
        `👉 *Tap here to view profile & connect:*`
      ];
      plainLines = [
        `🤝 RoomRoo Flatmate Profile: ${title}`,
        formattedLocation ? `📍 Preferred Suburb: ${formattedLocation}` : null,
        formattedPrice ? `💰 Budget: ${formattedPrice}` : null,
        '',
        `👉 Tap link to view profile & connect:`
      ];
      emailSubject = `RoomRoo Flatmate: ${title}`;
    } else {
      whatsappLines = [
        `🏠 *RoomRoo Accommodation*`,
        title ? `🛏️ *Property:* "${title}"` : null,
        formattedLocation ? `📍 *Location:* ${formattedLocation}` : null,
        formattedPrice ? `💰 *Rent:* ${formattedPrice}` : null,
        cleanCategory ? `🏷️ *Type:* ${cleanCategory}` : null,
        '',
        `👉 *Tap here to view room & book inspection:*`
      ];
      plainLines = [
        `🏠 RoomRoo Accommodation: ${title}`,
        formattedLocation ? `📍 Location: ${formattedLocation}` : null,
        formattedPrice ? `💰 Rent: ${formattedPrice}` : null,
        '',
        `👉 Tap link to view room & book inspection:`
      ];
      emailSubject = `RoomRoo Accommodation: ${title}`;
    }
  } else if (brand.brandKey === 'hiremate') {
    if (brand.type === 'candidate') {
      whatsappLines = [
        `🧑‍💼 *HireMate Candidate Profile*`,
        title ? `👤 *Candidate:* "${title}"` : null,
        cleanCategory ? `🛠️ *Skills / Roles:* ${cleanCategory}` : null,
        formattedPrice ? `💰 *Hourly Rate:* ${formattedPrice}` : null,
        formattedLocation ? `📍 *Location:* ${formattedLocation}` : null,
        '',
        `👉 *Tap here to view profile & Hire Candidate:*`
      ];
      plainLines = [
        `🧑‍💼 HireMate Candidate Profile: ${title}`,
        cleanCategory ? `🛠️ Skills / Roles: ${cleanCategory}` : null,
        formattedPrice ? `💰 Hourly Rate: ${formattedPrice}` : null,
        formattedLocation ? `📍 Location: ${formattedLocation}` : null,
        '',
        `👉 Tap link to view profile & Hire Candidate:`
      ];
      emailSubject = `HireMate Candidate Profile: ${title}`;
    } else if (brand.type === 'task') {
      whatsappLines = [
        `⚡ *HireMate Task Listing*`,
        title ? `📋 *Task:* "${title}"` : null,
        formattedPrice ? `💰 *Budget / Pay:* ${formattedPrice}` : null,
        formattedLocation ? `📍 *Location:* ${formattedLocation}` : null,
        cleanCategory ? `🏷️ *Category:* ${cleanCategory}` : null,
        '',
        `👉 *Tap here to view task & Make an Offer / Earn:*`
      ];
      plainLines = [
        `⚡ HireMate Task Listing: ${title}`,
        formattedPrice ? `💰 Budget / Pay: ${formattedPrice}` : null,
        formattedLocation ? `📍 Location: ${formattedLocation}` : null,
        '',
        `👉 Tap link to view task & Make an Offer / Earn:`
      ];
      emailSubject = `HireMate Task: ${title}`;
    } else {
      // Job Opening / Staff Request / Business Shift
      whatsappLines = [
        `💼 *HireMate Job Opportunity*`,
        title ? `📌 *Role:* "${title}"` : null,
        cleanCategory ? `🏢 *Category / Industry:* ${cleanCategory}` : null,
        formattedPrice ? `💰 *Pay Rate:* ${formattedPrice}` : null,
        formattedLocation ? `📍 *Location:* ${formattedLocation}` : null,
        '',
        `👉 *Tap here to view details & Apply Now:*`
      ];
      plainLines = [
        `💼 HireMate Job Opportunity: ${title}`,
        cleanCategory ? `🏢 Category / Industry: ${cleanCategory}` : null,
        formattedPrice ? `💰 Pay Rate: ${formattedPrice}` : null,
        formattedLocation ? `📍 Location: ${formattedLocation}` : null,
        '',
        `👉 Tap link to view details & Apply Now:`
      ];
      emailSubject = `HireMate Job Opportunity: ${title}`;
    }
  } else if (brand.brandKey === 'carease') {
    whatsappLines = [
      `🚗 *CarEase Vehicle Rental*`,
      title ? `🚘 *Car Model:* "${title}"` : null,
      formattedLocation ? `📍 *Location:* ${formattedLocation}` : null,
      formattedPrice ? `💰 *Rental Rate:* ${formattedPrice}` : null,
      cleanCategory ? `🏷️ *Vehicle Type:* ${cleanCategory}` : null,
      '',
      `👉 *Tap here to view details & book car:*`
    ];
    plainLines = [
      `🚗 CarEase Vehicle Rental: ${title}`,
      formattedLocation ? `📍 Location: ${formattedLocation}` : null,
      formattedPrice ? `💰 Rental Rate: ${formattedPrice}` : null,
      '',
      `👉 Tap link to view details & book car:`
    ];
    emailSubject = `CarEase Listing: ${title}`;
  } else {
    // Default StudentsUnion
    whatsappLines = [
      `🎓 *StudentsUnion AU*`,
      title ? `📌 *Listing:* "${title}"` : null,
      formattedPrice ? `💰 *Price:* ${formattedPrice}` : null,
      formattedLocation ? `📍 *Location:* ${formattedLocation}` : null,
      '',
      `👉 *Tap here to view details:*`
    ];
    plainLines = [
      `🎓 StudentsUnion AU: ${title}`,
      formattedPrice ? `💰 Price: ${formattedPrice}` : null,
      formattedLocation ? `📍 Location: ${formattedLocation}` : null,
      '',
      `👉 Tap link to view details:`
    ];
    emailSubject = `StudentsUnion AU: ${title}`;
  }

  const whatsappText = whatsappLines.filter(Boolean).join('\n');
  const plainText = plainLines.filter(Boolean).join('\n');

  return {
    brand,
    previewUrl,
    directUrl: url,
    whatsappText,
    plainText,
    emailSubject
  };
};
